import { type ReactNode } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import ResearchNav from '@/components/admin/research/ResearchNav'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { X } from 'lucide-react'

type Overview = {
  events: number
  organizations: number
  venues: number
  sponsors: number
  sponsor_revenue: number
}

type NumericStat = {
  key: string
  label: string
  money: boolean
  count: number
  min: number | null
  avg: number | null
  max: number | null
  total: number | null
}

type FieldReport = {
  key: string
  label: string
  answered: number
  values: { value: string; count: number }[]
}

type Filters = {
  year: string
  state: string
  city: string
  month: string
  day: string
  start_date: string
  end_date: string
}

type Option = { value: string; label: string }

type Props = {
  filters: Filters
  filter_options: {
    years: number[]
    states: string[]
    cities: string[]
    months: Option[]
    days: Option[]
  }
  overview: Overview
  numeric_stats: NumericStat[]
  breakdowns: FieldReport[]
}

const ANY = '__any__'

function money(value: number | null) {
  if (value === null) return '—'
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: value % 1 === 0 ? 0 : 2,
  })
}

function plain(value: number | null) {
  if (value === null) return '—'
  return value.toLocaleString('en-US')
}

export default function ResearchReportsIndex({
  filters,
  filter_options,
  overview,
  numeric_stats,
  breakdowns,
}: Props) {
  const hasFilters = Object.values(filters).some(Boolean)

  function applyFilters(next: Partial<Filters>) {
    const merged = { ...filters, ...next }
    router.get(
      '/admin/research/reports',
      Object.fromEntries(Object.entries(merged).filter(([, v]) => v !== '')),
      { preserveState: true },
    )
  }

  function filterSelect(label: string, key: keyof Filters, options: Option[]) {
    return (
      <Select
        value={filters[key] || ANY}
        onValueChange={value => applyFilters({ [key]: value === ANY ? '' : value })}
      >
        <SelectTrigger className="w-34" size="sm">
          <SelectValue placeholder={label} />
        </SelectTrigger>
        <SelectContent position="popper" align="end">
          <SelectGroup>
            <SelectItem value={ANY}>{label}: All</SelectItem>
            {options.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    )
  }

  const tiles = [
    { label: 'Events', value: plain(overview.events) },
    { label: 'Organizations', value: plain(overview.organizations) },
    { label: 'Venues Used', value: plain(overview.venues) },
    { label: 'Sponsors', value: plain(overview.sponsors) },
    { label: 'Sponsor Revenue', value: money(overview.sponsor_revenue) },
  ]

  const recordedStats = numeric_stats.filter(stat => stat.count > 0)

  return (
    <>
      <Head title="Research - Event Analytics" />
      <div>
        <h1 className="text-3xl font-semibold tracking-tight font-serif">Research Database</h1>
        <div className="mt-4">
          <ResearchNav />
        </div>

        <p className="text-muted-foreground">
          Analytics across every tracked data point. Events missing a value are left out of that
          field&rsquo;s numbers.
        </p>

        <div className="mt-4 flex flex-wrap items-end gap-2">
          {filterSelect(
            'Year',
            'year',
            filter_options.years.map(year => ({ value: String(year), label: String(year) })),
          )}
          {filterSelect(
            'State',
            'state',
            filter_options.states.map(state => ({ value: state, label: state })),
          )}
          {filterSelect(
            'City',
            'city',
            filter_options.cities.map(city => ({ value: city, label: city })),
          )}
          {filterSelect('Month', 'month', filter_options.months)}
          {filterSelect('Day', 'day', filter_options.days)}
          <div className="space-y-1">
            <Label htmlFor="report-start-date" className="text-xs text-muted-foreground">
              From
            </Label>
            <Input
              id="report-start-date"
              type="date"
              className="h-8 w-38"
              value={filters.start_date}
              onChange={e => applyFilters({ start_date: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="report-end-date" className="text-xs text-muted-foreground">
              To
            </Label>
            <Input
              id="report-end-date"
              type="date"
              className="h-8 w-38"
              value={filters.end_date}
              onChange={e => applyFilters({ end_date: e.target.value })}
            />
          </div>
          {hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => router.get('/admin/research/reports')}
            >
              <X className="mr-1 size-3.5" />
              Clear
            </Button>
          )}
        </div>

        <div className="mt-6 grid grid-cols-5 gap-4">
          {tiles.map(tile => (
            <div
              key={tile.label}
              className="rounded-2xl border border-black/10 bg-card p-4 shadow-xs"
            >
              <p className="text-xs text-muted-foreground">{tile.label}</p>
              <p className="mt-1 text-2xl font-semibold tracking-tight">{tile.value}</p>
            </div>
          ))}
        </div>

        {recordedStats.length > 0 && (
          <>
            <h2 className="mt-8 mb-3 text-lg font-semibold tracking-tight">Amounts</h2>
            <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
              {recordedStats.map(stat => {
                const format = stat.money ? money : plain
                return (
                  <div
                    key={stat.key}
                    className="rounded-2xl border border-black/10 bg-card p-4 shadow-xs"
                  >
                    <div className="flex items-baseline justify-between">
                      <p className="text-sm font-medium">{stat.label}</p>
                      <span className="text-xs text-muted-foreground">{stat.count} recorded</span>
                    </div>
                    <p className="mt-2 text-2xl font-semibold tracking-tight">{format(stat.avg)}</p>
                    <p className="text-xs text-muted-foreground">average</p>
                    <div className="mt-3 flex gap-4 text-xs text-muted-foreground">
                      <span>
                        Min <span className="font-medium text-foreground">{format(stat.min)}</span>
                      </span>
                      <span>
                        Max <span className="font-medium text-foreground">{format(stat.max)}</span>
                      </span>
                      {stat.total !== null && (
                        <span>
                          Total{' '}
                          <span className="font-medium text-foreground">{format(stat.total)}</span>
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}

        <h2 className="mt-8 mb-3 text-lg font-semibold tracking-tight">Most used</h2>
        <div className="grid grid-cols-2 gap-6 xl:grid-cols-3">
          {breakdowns.map(report => {
            const max = report.values[0]?.count ?? 0
            return (
              <Card key={report.key} className="self-start">
                <CardHeader>
                  <div className="flex items-baseline justify-between">
                    <CardTitle>
                      <Link
                        href={`/admin/research/reports/field?field=${encodeURIComponent(report.key)}`}
                        className="hover:underline"
                        title={`Open ${report.label} analytics`}
                      >
                        {report.label}
                      </Link>
                    </CardTitle>
                    <span className="text-xs text-muted-foreground">
                      {report.answered} recorded
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  {report.values.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No data recorded yet.</p>
                  ) : (
                    <ul className="space-y-2.5">
                      {report.values.map(({ value, count }) => {
                        const share =
                          report.answered > 0 ? Math.round((count / report.answered) * 100) : 0
                        return (
                          <li key={value}>
                            <Link
                              href={`/admin/research/reports/usage?field=${encodeURIComponent(report.key)}&value=${encodeURIComponent(value)}`}
                              className="group -mx-2 block rounded-lg px-2 py-1 transition-colors hover:bg-muted/60"
                              title={`See which organizations use ${value}`}
                            >
                              <div className="flex items-baseline justify-between gap-2 text-sm">
                                <span className="truncate font-medium group-hover:underline">
                                  {value}
                                </span>
                                <span className="shrink-0 text-muted-foreground">
                                  {count} · {share}%
                                </span>
                              </div>
                              <div className="mt-1 h-1.5 rounded-full bg-muted">
                                <div
                                  className="h-1.5 rounded-full bg-primary"
                                  style={{ width: `${max > 0 ? (count / max) * 100 : 0}%` }}
                                />
                              </div>
                            </Link>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      </div>
    </>
  )
}

ResearchReportsIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
